/**
 * Shopify Product Recommendations
 * Fetches and displays product recommendations using Shopify's native API
 * Emits the .product-recs-block card structure styled in base.css
 */

class ShopifyProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    // Hand-picked products are already rendered by the section, so there is
    // nothing to fetch; only the slider needs mounting.
    if (this.hasAttribute('data-static')) {
      if (this.querySelector('.splide')) {
        this.initializeSlider();
        this.classList.add('product-recommendations--loaded');
      } else {
        this.handleNoRecommendations();
      }
      return;
    }
    this.initializeRecommendations();
  }

  initializeRecommendations() {
    // Use IntersectionObserver for lazy loading
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations() {
    const url = this.dataset.url;
    if (!url) {
      this.handleNoRecommendations();
      return;
    }

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('shopify-product-recommendations');
        
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          
          // Trigger lazy loading for images if available
          if (window.lazyloadPolyfill) {
            window.lazyloadPolyfill(this);
          }
          
          // Initialize any sliders or interactive elements
          this.initializeSlider();
          
          // Add loaded class for styling
          this.classList.add('product-recommendations--loaded');
        } else {
          this.handleNoRecommendations();
        }
      })
      .catch(error => {
        console.error('Error fetching product recommendations:', error);
        this.handleNoRecommendations();
      });
  }

  handleNoRecommendations() {
    // Hide the section if no recommendations are available
    this.style.display = 'none';
    this.classList.add('product-recommendations--loaded');
  }

  initializeSlider() {
    const splideElement = this.querySelector('.splide');
    if (!splideElement) return;

    // Wait for Splide to be available
    if (typeof Splide === 'undefined') {
      // Retry after a short delay if Splide isn't loaded yet
      setTimeout(() => this.initializeSlider(), 100);
      return;
    }

    const splide = new Splide(splideElement, {
      type: 'slide',
      perPage: 4,
      perMove: 1,
      gap: '1rem',
      pagination: false,
      drag: true,
      breakpoints: {
        999: {
          perPage: 3,
        },
        680: {
          perPage: 2,
          gap: '0.5rem',
        },
        480: {
          perPage: 2,
          gap: '0.5rem',
        },
      },
    });

    splide.mount();

    // Dispatch event for any additional initialization
    this.dispatchEvent(new CustomEvent('recommendations:loaded', {
      bubbles: true,
      detail: { splide }
    }));
  }
}

customElements.define('shopify-product-recommendations', ShopifyProductRecommendations);

